import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook that runs effect only after authentication is complete
 * Similar to useEffect, but automatically waits for auth to finish loading
 */
export function useAuthenticatedEffect(
  effect: () => void | (() => void),
  deps?: React.DependencyList
): void {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);

  // Poll for token in sessionStorage when auth is loading or user is set
  useEffect(() => {
    if (isLoading || !user || !isAuthenticated) {
      setTokenReady(false);
      return;
    }

    // Check if token is in sessionStorage
    const checkToken = () => {
      try {
        return !!sessionStorage.getItem('authToken');
      } catch {
        return false;
      }
    };

    // Check immediately
    if (checkToken()) {
      setTokenReady(true);
      return;
    }

    // Poll every 100ms until token is found (max 2 seconds)
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      if (checkToken()) {
        setTokenReady(true);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isLoading, user, isAuthenticated]);

  useEffect(() => {
    // Wait for auth to finish loading AND user to be set AND authenticated AND token ready
    if (isLoading || !user || !isAuthenticated || !tokenReady) {
      console.log('[useAuthenticatedEffect] Skipping effect - auth not ready', { isLoading, user: !!user, isAuthenticated, tokenReady });
      return;
    }

    console.log('[useAuthenticatedEffect] Running effect, deps changed');
    // Run the effect
    return effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user, isAuthenticated, tokenReady, ...(deps || [])]);
}
